import { useEffect, useState } from "react";
import {
  Building2,
  Mail,
  ChevronDown,
  Cpu,
  Cloud,
  Shield,
  Briefcase,
  Newspaper,
  UserCog,
  AlertTriangle,
  PlayCircle,
  Copy,
  Check,
  Trash2,
  Server,
  TrendingUp,
  Linkedin,
  Phone,
  RefreshCw,
  Sparkles,
  Workflow,
  Search,
  DollarSign,
} from "lucide-react";

// Format ISO timestamp or relative string into a short relative date label.
function formatPosted(value) {
  if (!value) return "";
  try {
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return String(value);
    const days = Math.floor((Date.now() - dt.getTime()) / 86400000);
    if (days <= 0) return "today";
    if (days === 1) return "yesterday";
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  } catch {
    return String(value);
  }
}
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import DraftEmailDialog from "@/components/DraftEmailDialog";
import EnrollSequenceDialog from "@/components/EnrollSequenceDialog";

function scoreTier(score) {
  if (score >= 70)
    return { label: "High Priority", bg: "bg-score-high-bg", fg: "text-score-high-fg", border: "border-score-high-border", dot: "bg-[#2E7D32]" };
  if (score >= 40)
    return { label: "Medium Priority", bg: "bg-score-med-bg", fg: "text-score-med-fg", border: "border-score-med-border", dot: "bg-[#F57F17]" };
  return { label: "Low Priority", bg: "bg-score-low-bg", fg: "text-score-low-fg", border: "border-score-low-border", dot: "bg-[#C62828]" };
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  if (diff < 86400 * 365) return `${Math.floor(diff / (86400 * 30))}mo ago`;
  return d.toLocaleDateString();
}

function CopyButton({ text, label = "copy", testId }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text || "");
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error("Could not copy");
    }
  };
  return (
    <button
      data-testid={testId}
      onClick={onCopy}
      className="opacity-0 group-hover:opacity-100 transition-opacity bg-ink-100 text-ink-700 hover:bg-ink-200 text-[10px] uppercase tracking-widestx font-semibold px-2 py-1 rounded-sm inline-flex items-center gap-1"
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? "copied" : label}
    </button>
  );
}

function Tag({ children, variant = "default", testId }) {
  const styles = {
    default: "bg-ink-50 text-ink-700 border-ink-100",
    intent: "bg-blue-50 text-blue-700 border-blue-200",
    funding: "bg-purple-50 text-purple-700 border-purple-200",
    tech: "bg-ink-100 text-ink-700 border-ink-200",
  };
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-widestx border ${styles[variant]}`}
    >
      {children}
    </span>
  );
}

const EMAIL_STATUS_STYLE = {
  verified:    { bg: "bg-[#E8F5E9]", fg: "text-[#2E7D32]", label: "Verified" },
  likely_to_engage: { bg: "bg-[#E8F5E9]", fg: "text-[#2E7D32]", label: "Engaged" },
  guessed:     { bg: "bg-[#FFF8E1]", fg: "text-[#F57F17]", label: "Inferred" },
  extrapolated:{ bg: "bg-[#FFF8E1]", fg: "text-[#F57F17]", label: "Inferred" },
  unverified:  { bg: "bg-ink-100",   fg: "text-ink-700",   label: "Unverified" },
  unavailable: { bg: "bg-[#FFEBEE]", fg: "text-[#C62828]", label: "Not found" },
};

function EmailStatusBadge({ status, score }) {
  const s = EMAIL_STATUS_STYLE[status] || EMAIL_STATUS_STYLE.unverified;
  return (
    <span
      data-testid="email-status-badge"
      title={`LeadIQ email status: ${status} (${score}/100)`}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-semibold uppercase tracking-widestx ${s.bg} ${s.fg}`}
    >
      {s.label}
      {score > 0 && <span className="opacity-70 normal-case tracking-normal">{score}</span>}
    </span>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-3.5 h-3.5 text-ink-400" strokeWidth={2} />
        <h4 className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">{title}</h4>
      </div>
      <div>{children}</div>
    </div>
  );
}

function StackChip({ label, items }) {
  if (!items || !items.length) return null;
  return (
    <div className="flex items-start gap-3">
      <div className="text-[10px] uppercase tracking-wider font-mono text-ink-400 w-24 mt-1 shrink-0 leading-tight">
        {label}
      </div>
      <div className="flex flex-wrap gap-1.5 min-w-0 flex-1">
        {items.map((it, i) => (
          <Tag key={i} variant="tech">{it}</Tag>
        ))}
      </div>
    </div>
  );
}

export default function LeadCard({ lead, index, onDelete, onReenrich, onFindEmail, onRevealFunding, reenriching }) {
  const { user } = useAuth();
  // Funding intel is a paid-tier perk: Growth / Scale / Enterprise get it free.
  const fundingIncludedInPlan = ["growth", "scale", "enterprise"].includes(
    (user?.tier || "").toLowerCase()
  );
  const [open, setOpen] = useState(false);
  const [draftOpen, setDraftOpen] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [activeSeq, setActiveSeq] = useState(null);
  const [leadCooldown, setLeadCooldown] = useState(null);
  const [findingEmail, setFindingEmail] = useState(false);
  const [revealingFunding, setRevealingFunding] = useState(false);
  const [findingMobile, setFindingMobile] = useState(false);
  const [mobileNumber, setMobileNumber] = useState(
    ((lead.enrichment || {}).contact || {}).mobile?.number || null
  );

  // Poll a pending mobile lookup until it settles or times out (~60s)
  const startMobilePoll = async (lookupId) => {
    const started = Date.now();
    while (Date.now() - started < 60000) {
      await new Promise((r) => setTimeout(r, 3500));
      try {
        const { data } = await http.get(`/mobile-lookups/${lookupId}`);
        if (data.state === "settled") {
          setMobileNumber(data.phone);
          toast.success("Mobile revealed · 1 credit used");
          return;
        }
        if (data.state === "refunded") {
          toast.info("No mobile found — your credit was refunded.");
          return;
        }
        if (data.state === "error") {
          toast.error("Mobile lookup failed — no credit charged.");
          return;
        }
      } catch (_) {
        // transient network — keep polling
      }
    }
    toast.info("Still working on it — check back in a minute.");
  };

  const handleFindMobile = async (ev) => {
    ev.stopPropagation();
    if (findingMobile || mobileNumber) return;
    setFindingMobile(true);
    try {
      const { data } = await http.post(`/leads/${lead.id}/find-mobile`);
      if (data.lookup_id) {
        toast.info("Finding mobile… (30-90s)");
        await startMobilePoll(data.lookup_id);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && detail?.code === "no_mobile_credits") {
        toast.error("No mobile credits. Buy a pack from Settings → Find Mobile.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Mobile lookup failed");
      }
    } finally {
      setFindingMobile(false);
    }
  };

  // Auto-fetch funding intel for Growth+ users so it's visible without a
  // manual "Reveal" click. Fires once per card. Staggered by index (300ms
  // apart) to avoid a thundering herd when the dashboard first loads.
  // Server-side cache (30d positive, 7d negative) makes repeat page-views free.
  useEffect(() => {
    if (!fundingIncludedInPlan) return;
    if (!onRevealFunding || !lead?.id) return;
    const hasFunding = !!(lead.enrichment || {}).funding_intel;
    const hasKeys = !!(lead.input?.company || lead.input?.domain);
    if (hasFunding || !hasKeys) return;
    if (revealingFunding) return;
    const delay = Math.min(300 * (index % 20), 3000);
    let alive = true;
    setRevealingFunding(true);
    const t = setTimeout(async () => {
      if (!alive) return;
      try {
        await onRevealFunding(lead.id, { silent: true });
      } finally {
        if (alive) setRevealingFunding(false);
      }
    }, delay);
    return () => {
      alive = false;
      clearTimeout(t);
    };
    // Intentionally only key on lead.id + tier — we don't want to re-fire
    // when onRevealFunding identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id, fundingIncludedInPlan]);

  // Lazily fetch the lead's active sequence enrollment (if any) the first time
  // the card is expanded — keeps the dashboard list fast and avoids N+1 calls
  // for unopened leads.
  useEffect(() => {
    if (!open || activeSeq !== null || !lead?.id) return;
    let alive = true;
    http
      .get(`/leads/${lead.id}/enrollment`)
      .then((r) => {
        if (!alive) return;
        setActiveSeq(r.data?.active ? r.data.enrollment : false);
        setLeadCooldown(r.data?.cooldown || null);
      })
      .catch(() => alive && setActiveSeq(false));
    return () => {
      alive = false;
    };
  }, [open, lead?.id, activeSeq]);

  // After the EnrollSequenceDialog closes (it manages its own enrollment), refresh.
  useEffect(() => {
    if (enrollOpen) return;
    if (!open || !lead?.id) return;
    let alive = true;
    http
      .get(`/leads/${lead.id}/enrollment`)
      .then((r) => {
        if (!alive) return;
        setActiveSeq(r.data?.active ? r.data.enrollment : false);
        setLeadCooldown(r.data?.cooldown || null);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [enrollOpen, open, lead?.id]);
  const e = lead.enrichment || {};
  const i = lead.input || {};
  const c = e.contact || {};
  // Prefer LeadIQ-found email/title/linkedin if present, otherwise fall back to user input
  const displayEmail = c.email || i.email;
  const displayTitle = c.title || i.title;
  const displayLinkedIn = c.linkedin_url || i.linkedin_url;
  // If the displayed email is the user's input (Apollo didn't return one), the
  // Apollo email_status (e.g. "unavailable") doesn't apply — treat as unverified.
  const emailIsApollo = !!c.email;
  const emailStatus = displayEmail
    ? emailIsApollo
      ? (c.email_status || "unverified")
      : "unverified"
    : "unavailable";
  const emailScore = emailIsApollo ? (c.email_score ?? 0) : 0;
  // The "Find a better email" CTA appears when:
  //  - the current email is not trustworthy (anything other than verified), OR
  //  - there is no email at all on this lead.
  // It needs a name + (domain or company) to call the email finder.
  const canFindBetterEmail =
    !!onFindEmail &&
    !!(i.name && (i.domain || i.company)) &&
    (!displayEmail || emailStatus !== "verified");
  const tier = scoreTier(e.opportunity_score || 0);
  const fitTier = scoreTier(e.fit_score || 0);
  const hasFitScore = e.fit_score !== undefined && e.fit_score !== null && !!e._has_profile;
  const hiring = e.intent_signals?.hiring_signals || [];
  const hiringSource = e.intent_signals?.hiring_source || null; // "leadiq" when real data
  const funding = e.funding_intel || null;
  const news = e.intent_signals?.recent_news || [];
  const tags = e.tags || [];

  return (
    <div
      data-testid={`lead-card-${index}`}
      className="bg-white border border-ink-100 rounded-sm transition-all duration-200 group fade-up"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {/* collapsed row */}
      <button
        onClick={() => setOpen(!open)}
        data-testid={`lead-card-toggle-${index}`}
        className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-ink-50/40 transition-colors"
      >
        <div className={`w-2 h-2 rounded-sm ${tier.dot} shrink-0`} />

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <h3 className="font-display font-extrabold text-ink-900 text-base truncate">
              {i.company || "Unknown company"}
            </h3>
            <span className="text-[11px] font-mono text-ink-400 truncate">{i.domain}</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-500 mt-1">
            {i.name && <span className="text-ink-700 font-medium">{i.name}</span>}
            {displayTitle && <span className="text-ink-400">{displayTitle}</span>}
            {displayEmail && (
              <span
                data-testid={`lead-email-${index}`}
                className="inline-flex items-center gap-1.5 max-w-full min-w-0"
              >
                <span className="font-mono text-ink-700 truncate max-w-[220px]">
                  {displayEmail}
                </span>
                <EmailStatusBadge status={emailStatus} score={emailScore} />
              </span>
            )}
            {canFindBetterEmail && (
              <button
                type="button"
                data-testid={`find-email-header-${index}`}
                disabled={findingEmail}
                onClick={async (ev) => {
                  ev.stopPropagation();
                  setFindingEmail(true);
                  try {
                    await onFindEmail(lead.id);
                  } finally {
                    setFindingEmail(false);
                  }
                }}
                title={displayEmail
                  ? "This email failed validation. Spend 1 credit to find the correct one — refunded if not found."
                  : "No email on file. Spend 1 credit to discover one via LeadIQ — refunded if not found."}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] font-semibold uppercase tracking-wide transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  displayEmail
                    ? "border-brand/30 bg-brand/5 hover:bg-brand/10 text-brand"
                    : "border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700"
                }`}
              >
                <Search className={`w-3 h-3 ${findingEmail ? "animate-pulse" : ""}`} />
                {findingEmail ? "Finding…" : (displayEmail ? "Find better" : "Find email")}
                <span className="font-mono opacity-70">· 1 cr</span>
              </button>
            )}
            {displayLinkedIn && (
              <a
                href={displayLinkedIn}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(ev) => ev.stopPropagation()}
                data-testid={`lead-linkedin-${index}`}
                className="inline-flex items-center gap-1 text-[#0A66C2] hover:underline"
              >
                <Linkedin className="w-3.5 h-3.5" />
                <span className="text-[11px] font-mono">LinkedIn</span>
              </a>
            )}
            {mobileNumber ? (
              <a
                href={`tel:${mobileNumber}`}
                onClick={(ev) => ev.stopPropagation()}
                data-testid={`lead-mobile-${index}`}
                className="inline-flex items-center gap-1 text-emerald-700 hover:underline"
                title="Click to call"
              >
                <Phone className="w-3.5 h-3.5" />
                <span className="text-[11px] font-mono">{mobileNumber}</span>
                <span className="text-[9px] uppercase tracking-widestx bg-emerald-50 border border-emerald-200 px-1 rounded-sm">
                  verified
                </span>
              </a>
            ) : (
              <button
                type="button"
                data-testid={`find-mobile-${index}`}
                disabled={findingMobile}
                onClick={handleFindMobile}
                title="Reveal mobile number via LeadIQ · charged only if we find one · non-expiring credits"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] font-semibold uppercase tracking-wide transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800"
              >
                <Phone className={`w-3 h-3 ${findingMobile ? "animate-pulse" : ""}`} />
                {findingMobile ? "Finding…" : "Find mobile"}
                <span className="font-mono opacity-70">· 1 cr</span>
              </button>
            )}
          </div>
          {(hiring.length > 0 || news.length > 0 || tags.length > 0 || funding) && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {funding?.last_round?.round && (
                <Tag variant="funding" testId={`funding-tag-${index}`}>
                  💰 {funding.last_round.round}
                  {funding.last_round.amount_pretty ? ` · $${funding.last_round.amount_pretty}` : ""}
                </Tag>
              )}
              {hiring.length > 0 && <Tag variant="intent">{hiring.length} hiring signal{hiring.length === 1 ? "" : "s"}</Tag>}
              {news.length > 0 && <Tag variant="funding">{news.length} news item{news.length === 1 ? "" : "s"}</Tag>}
              {tags.slice(0, 3).map((t, ix) => (
                <Tag key={ix}>{t}</Tag>
              ))}
            </div>
          )}
          {(lead.re_enriched_at || lead.created_at) && (
            <div className="text-[10px] font-mono text-ink-400 mt-2 flex items-center gap-3" data-testid={`lead-enriched-meta-${index}`}>
              <span className="inline-flex items-center gap-1">
                <RefreshCw className="w-2.5 h-2.5" />
                {lead.re_enriched_at ? "Re-enriched" : "Enriched"} {timeAgo(lead.re_enriched_at || lead.created_at)}
              </span>
              {lead.re_enriched_at && lead.created_at && (
                <span className="text-ink-300">· first added {timeAgo(lead.created_at)}</span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <div className={`px-3 py-2 rounded-sm border ${tier.bg} ${tier.border} text-right min-w-[72px]`}>
            <div className={`font-mono text-2xl font-black tracking-tighter leading-none ${tier.fg}`}>
              {e.opportunity_score ?? 0}
            </div>
            <div className={`text-[9px] uppercase tracking-widestx font-semibold ${tier.fg} mt-0.5`}>
              opp · /100
            </div>
          </div>
          {hasFitScore && (
            <div
              data-testid={`lead-fit-score-${index}`}
              className={`px-3 py-2 rounded-sm border ${fitTier.bg} ${fitTier.border} text-right min-w-[72px]`}
            >
              <div className={`font-mono text-2xl font-black tracking-tighter leading-none ${fitTier.fg}`}>
                {e.fit_score ?? 0}
              </div>
              <div className={`text-[9px] uppercase tracking-widestx font-semibold ${fitTier.fg} mt-0.5`}>
                fit · /100
              </div>
            </div>
          )}
          {displayEmail && (
            <button
              type="button"
              onClick={(ev) => { ev.stopPropagation(); setDraftOpen(true); }}
              data-testid={`draft-email-quick-${index}`}
              title="Draft AI email (1 credit)"
              className="hidden sm:inline-flex items-center gap-1.5 h-[52px] px-3 rounded-sm bg-brand hover:bg-brand-hover text-white text-xs font-semibold tracking-wide transition-colors"
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span className="hidden md:inline">Draft</span>
              <span className="hidden lg:inline text-[10px] font-mono opacity-80 ml-1">1 cr</span>
            </button>
          )}
          <ChevronDown
            className={`w-4 h-4 text-ink-400 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {/* expanded panel */}
      {open && (
        <div
          data-testid={`lead-card-expanded-${index}`}
          className="border-t border-ink-100 bg-ink-50/40 p-6 grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-8"
        >
          {/* col 1 — overview + tech */}
          <div className="space-y-6">
            <Section icon={Building2} title="Company overview">
              <div className="text-sm text-ink-900 font-medium">
                {i.company} · <span className="text-ink-500 font-normal">{e.company_overview?.industry || "Unknown industry"}</span>
              </div>
              <div className="text-xs font-mono text-ink-500 mt-1">
                {e.company_overview?.company_size || "Unknown"} · {i.domain}
              </div>
              {e.company_overview?.summary && (
                <p className="text-sm text-ink-700 leading-relaxed mt-3">{e.company_overview.summary}</p>
              )}
            </Section>

            <Section
              icon={DollarSign}
              title={
                <span className="inline-flex items-center gap-2">
                  Funding intel
                  {funding && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-700 text-[9px] uppercase font-mono tracking-widestx"
                      title="Loaded from LeadIQ"
                    >
                      <Check className="w-2.5 h-2.5" /> live · leadiq
                    </span>
                  )}
                </span>
              }
            >
              {!funding ? (
                <div className="bg-purple-50/50 border border-purple-200 rounded-sm p-3">
                  {fundingIncludedInPlan ? (
                    revealingFunding ? (
                      <div className="text-[11px] text-purple-700 italic inline-flex items-center gap-2" data-testid={`funding-loading-${index}`}>
                        <RefreshCw className="w-3 h-3 animate-spin" /> Loading funding intel…
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <p className="text-xs text-ink-600 leading-relaxed">
                          No funding intel available for this company.
                        </p>
                        <p className="text-[10px] text-ink-400">
                          Included in your plan · pulled from company filings, Wikipedia, and open-web sources.
                        </p>
                      </div>
                    )
                  ) : (
                    <>
                      <p className="text-xs text-ink-600 leading-relaxed mb-2.5">
                        Reveal funding rounds, revenue, leadership, competitors, recent news, and acquisitions.
                      </p>
                      {onRevealFunding && (i.company || i.domain) ? (
                        <button
                          type="button"
                          data-testid={`reveal-funding-${index}`}
                          disabled={revealingFunding}
                          onClick={async (ev) => {
                            ev.stopPropagation();
                            setRevealingFunding(true);
                            try {
                              await onRevealFunding(lead.id);
                            } finally {
                              setRevealingFunding(false);
                            }
                          }}
                          title="Spend 1 LeadIQ credit to fetch funding intel. Refunded if not found."
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-purple-300 bg-white hover:bg-purple-50 text-purple-700 text-[11px] font-semibold uppercase tracking-wide transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <DollarSign className={`w-3 h-3 ${revealingFunding ? "animate-pulse" : ""}`} />
                          {revealingFunding ? "Revealing…" : "Reveal funding intel"}
                          <span className="text-[10px] font-mono opacity-70 ml-0.5">1 cr · refund on miss</span>
                        </button>
                      ) : (
                        <div className="text-[11px] text-ink-400 italic">Need company name or domain to look up funding.</div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {funding.last_round && (
                    <div className="bg-purple-50/40 border border-purple-200 rounded-sm p-2.5">
                      <div className="text-[10px] uppercase font-mono text-purple-600 tracking-widestx mb-0.5">Last round</div>
                      <div className="text-sm font-semibold text-ink-900">
                        {funding.last_round.round || "—"}
                        {funding.last_round.amount_pretty && (
                          <span className="text-purple-700"> · ${funding.last_round.amount_pretty}</span>
                        )}
                      </div>
                      {funding.last_round.date && (
                        <div className="text-[11px] font-mono text-ink-500 mt-0.5">{formatPosted(funding.last_round.date)}</div>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {funding.total_funding_pretty && (
                      <div>
                        <div className="text-[10px] uppercase font-mono text-ink-500">Total funding</div>
                        <div className="font-semibold text-ink-900">${funding.total_funding_pretty}</div>
                      </div>
                    )}
                    {funding.revenue_pretty && (
                      <div>
                        <div className="text-[10px] uppercase font-mono text-ink-500">Revenue</div>
                        <div className="font-semibold text-ink-900">${funding.revenue_pretty}</div>
                      </div>
                    )}
                    {funding.headquarters && (
                      <div className="col-span-2">
                        <div className="text-[10px] uppercase font-mono text-ink-500">HQ · Founded</div>
                        <div className="text-ink-700">{funding.headquarters} · {funding.founded || "—"}</div>
                      </div>
                    )}
                  </div>
                  {funding.leadership?.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase font-mono text-ink-500 mb-1">Leadership</div>
                      <ul className="space-y-0.5">
                        {funding.leadership.slice(0, 4).map((p, ix) => (
                          <li key={ix} className="text-xs text-ink-700">
                            <span className="font-medium">{p.name}</span>{" "}
                            <span className="text-ink-500">· {p.title}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {funding.competitors?.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase font-mono text-ink-500 mb-1">Top competitors</div>
                      <div className="flex flex-wrap gap-1">
                        {funding.competitors.slice(0, 6).map((c, ix) => (
                          <span key={ix} className="text-[11px] px-1.5 py-0.5 rounded-sm bg-ink-50 border border-ink-200 text-ink-700">
                            {c}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Section>

            <Section
              icon={Cpu}
              title={
                <span className="inline-flex items-center gap-2">
                  Tech stack
                  {e.tech_stack?.email_stack?.sources?.length > 0 && (
                    <span
                      data-testid={`esp-source-dns-${index}`}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-700 text-[9px] uppercase font-mono tracking-widestx"
                      title="Email stack sourced from public DNS records (SPF / MX) — verifiable"
                    >
                      <Check className="w-2.5 h-2.5" /> live · dns
                    </span>
                  )}
                </span>
              }
            >
              <div className="space-y-2">
                <StackChip label="Cloud" items={e.tech_stack?.cloud} />
                <StackChip label="CRM" items={e.tech_stack?.crm} />
                <StackChip label="Security" items={e.tech_stack?.security} />
                <StackChip label="Infra" items={e.tech_stack?.infrastructure} />
                {e.tech_stack?.email_stack?.corporate && (
                  <StackChip
                    label="Mail host"
                    items={[e.tech_stack.email_stack.corporate]}
                  />
                )}
                {e.tech_stack?.email_stack?.marketing_esp?.length > 0 && (
                  <StackChip
                    label="Marketing ESP"
                    items={e.tech_stack.email_stack.marketing_esp}
                  />
                )}
                {e.tech_stack?.email_stack?.transactional_esp?.length > 0 && (
                  <StackChip
                    label="Transactional"
                    items={e.tech_stack.email_stack.transactional_esp}
                  />
                )}
                {e.tech_stack?.email_stack?.support_esp?.length > 0 && (
                  <StackChip
                    label="Support email"
                    items={e.tech_stack.email_stack.support_esp}
                  />
                )}
                <StackChip label="Other" items={e.tech_stack?.other} />
                {!e.tech_stack ||
                  (Object.values(e.tech_stack).every((v) => !v || (Array.isArray(v) && !v.length)) && (
                    <div className="text-xs text-ink-400 italic">No tech stack signals found</div>
                  ))}
              </div>
            </Section>

            {e.products?.length > 0 && (
              <Section icon={Briefcase} title="Products & services">
                <ul className="space-y-1 text-sm text-ink-700">
                  {e.products.slice(0, 6).map((p, ix) => (
                    <li key={ix}>· {p}</li>
                  ))}
                </ul>
              </Section>
            )}
          </div>

          {/* col 2 — intent signals + news */}
          <div className="space-y-6">
            <Section
              icon={TrendingUp}
              title={
                <span className="inline-flex items-center gap-2">
                  Intent signals
                  {hiringSource === "leadiq" && (
                    <span
                      data-testid={`hiring-source-leadiq-${index}`}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-700 text-[9px] uppercase font-mono tracking-widestx"
                      title="Real job postings sourced from LeadIQ (not LLM-generated)"
                    >
                      <Check className="w-2.5 h-2.5" /> live · leadiq
                    </span>
                  )}
                </span>
              }
            >
              {hiring.length > 0 ? (
                <div className="space-y-2.5">
                  {hiring.slice(0, 5).map((h, ix) => {
                    const role = h.role || h.title || "(role)";
                    const posted = h.posted;
                    const url = h.url;
                    const loc = h.location;
                    const remote = h.remote;
                    const isReal = h.source === "leadiq";
                    return (
                      <div key={ix} className="text-sm">
                        {url && isReal ? (
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`hiring-link-${index}-${ix}`}
                            className="text-ink-900 font-medium hover:text-brand hover:underline inline-flex items-center gap-1"
                            onClick={(ev) => ev.stopPropagation()}
                          >
                            → {role}
                          </a>
                        ) : (
                          <div className="text-ink-900 font-medium">→ {role}</div>
                        )}
                        <div className="text-[11px] font-mono text-ink-400 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          {posted && <span>{formatPosted(posted)}</span>}
                          {loc && <span className="text-ink-500">· {loc}</span>}
                          {remote && (
                            <span className="px-1 py-px rounded-sm bg-ink-100 text-ink-600 text-[9px] uppercase">remote</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-xs text-ink-400 italic">
                  {hiringSource === "leadiq"
                    ? "Company has no open roles right now"
                    : "No recent job postings found"}
                </div>
              )}
            </Section>

            <Section icon={Newspaper} title="Recent news">
              {news.length > 0 ? (
                <ul className="space-y-2">
                  {news.slice(0, 5).map((n, ix) => (
                    <li key={ix} className="text-sm text-ink-700">
                      <div className="font-medium">· {n.headline}</div>
                      <div className="text-[11px] font-mono text-ink-400 mt-0.5">
                        {n.date} {n.source ? `· ${n.source}` : ""}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-xs text-ink-400 italic">No recent news found</div>
              )}
            </Section>

            {e.intent_signals?.events_attended?.length > 0 && (
              <Section icon={PlayCircle} title="Events">
                <div className="flex flex-wrap gap-1.5">
                  {e.intent_signals.events_attended.map((ev, ix) => (
                    <Tag key={ix} variant="intent">{ev}</Tag>
                  ))}
                </div>
              </Section>
            )}
          </div>

          {/* col 3 — committee + playbook */}
          <div className="space-y-6">
            <Section icon={UserCog} title="Buying committee">
              {e.buying_committee?.length > 0 ? (
                <ul className="space-y-2">
                  {e.buying_committee.slice(0, 5).map((b, ix) => (
                    <li key={ix} className="text-sm">
                      <div className="text-ink-900 font-medium">→ {b.role}</div>
                      <div className="text-xs text-ink-500 mt-0.5">{b.rationale}</div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-xs text-ink-400 italic">No committee inferred</div>
              )}
            </Section>

            {e.pain_points?.length > 0 && (
              <Section icon={AlertTriangle} title="Pain points (inferred)">
                <ul className="space-y-1 text-sm text-ink-700">
                  {e.pain_points.slice(0, 5).map((p, ix) => (
                    <li key={ix}>· {p}</li>
                  ))}
                </ul>
              </Section>
            )}

            <Section icon={PlayCircle} title="Engagement playbook">
              {e.engagement_playbook && (
                <div className="space-y-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">Timing</div>
                    <div className="text-ink-900 mt-0.5">{e.engagement_playbook.timing || "—"}</div>
                  </div>
                  {e.engagement_playbook.opening_line && (
                    <div className="group">
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">Opening line</div>
                        <CopyButton
                          text={e.engagement_playbook.opening_line}
                          testId={`copy-opening-${index}`}
                        />
                      </div>
                      <div className="font-mono text-[13px] text-ink-900 mt-1 italic leading-relaxed border-l-2 border-brand pl-3">
                        “{e.engagement_playbook.opening_line}”
                      </div>
                    </div>
                  )}
                  {e.engagement_playbook.value_prop && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">Value prop</div>
                      <div className="text-ink-700 mt-0.5">{e.engagement_playbook.value_prop}</div>
                    </div>
                  )}
                  {e.engagement_playbook.case_study_angle && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">Case study angle</div>
                      <div className="text-ink-700 mt-0.5">{e.engagement_playbook.case_study_angle}</div>
                    </div>
                  )}
                </div>
              )}
              <Button
                onClick={(ev) => { ev.stopPropagation(); setDraftOpen(true); }}
                data-testid={`draft-email-button-${index}`}
                className="mt-4 w-full h-10 rounded-sm bg-brand hover:bg-brand-hover text-white font-medium tracking-wide group"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Draft AI email
                <span className="ml-auto text-[10px] font-mono opacity-80">1 credit</span>
              </Button>
              <Button
                variant="outline"
                onClick={(ev) => { ev.stopPropagation(); setEnrollOpen(true); }}
                data-testid={`enroll-sequence-button-${index}`}
                className={`mt-2 w-full h-10 rounded-sm font-medium tracking-wide ${
                  activeSeq
                    ? "border-[#F0C36D] bg-[#FFF8E5] text-[#7A4F00] hover:bg-[#FFF1CC]"
                    : leadCooldown
                      ? "border-[#FFCDD2] bg-[#FFEBEE] text-[#C62828] hover:bg-[#FFD2D2]"
                      : "border-ink-200 text-ink-700 hover:bg-ink-50"
                }`}
              >
                <Workflow className="w-4 h-4 mr-2" />
                {activeSeq
                  ? `In sequence: ${activeSeq.sequence_name}`
                  : leadCooldown
                    ? `Cooldown until ${leadCooldown.until ? new Date(leadCooldown.until).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "soon"}`
                    : "Enroll in sequence"}
              </Button>
            </Section>

            <div className={`p-4 border ${tier.border} ${tier.bg} rounded-sm`}>
              <div className="flex items-center justify-between">
                <div className={`text-[10px] uppercase tracking-widestx font-semibold ${tier.fg}`}>
                  Opportunity score
                </div>
                <div className={`font-mono font-black text-2xl tracking-tighter ${tier.fg}`}>
                  {e.opportunity_score ?? 0}/100
                </div>
              </div>
              {e.score_rationale && (
                <p className={`text-xs ${tier.fg} mt-2 opacity-80`}>{e.score_rationale}</p>
              )}
            </div>

            {hasFitScore && (
              <div
                data-testid={`lead-fit-block-${index}`}
                className={`p-4 border ${fitTier.border} ${fitTier.bg} rounded-sm`}
              >
                <div className="flex items-center justify-between">
                  <div className={`text-[10px] uppercase tracking-widestx font-semibold ${fitTier.fg} inline-flex items-center gap-1.5`}>
                    Fit score · personalized
                  </div>
                  <div className={`font-mono font-black text-2xl tracking-tighter ${fitTier.fg}`}>
                    {e.fit_score ?? 0}/100
                  </div>
                </div>
                {e.fit_rationale && (
                  <p className={`text-xs ${fitTier.fg} mt-2 opacity-80`}>{e.fit_rationale}</p>
                )}
              </div>
            )}

            {/* footer actions */}
            <div className="flex items-center justify-between gap-3 pt-2 border-t border-ink-100 flex-wrap">
              <div className="group flex items-center gap-2 min-w-0 flex-wrap">
                {displayEmail && (
                  <>
                    <Mail className="w-3.5 h-3.5 text-ink-400 shrink-0" />
                    <span className="font-mono text-xs text-ink-700 truncate max-w-full break-all">{displayEmail}</span>
                    <EmailStatusBadge status={emailStatus} score={emailScore} />
                    <CopyButton text={displayEmail} label="copy email" testId={`copy-email-${index}`} />
                  </>
                )}
                {!displayEmail && (
                  <>
                    <Mail className="w-3.5 h-3.5 text-ink-400 shrink-0" />
                    <span className="text-xs italic text-ink-400">No email on file</span>
                  </>
                )}
                {canFindBetterEmail && (
                  <button
                    type="button"
                    data-testid={`find-better-email-${index}`}
                    disabled={findingEmail}
                    onClick={async (ev) => {
                      ev.stopPropagation();
                      setFindingEmail(true);
                      try {
                        await onFindEmail(lead.id);
                      } finally {
                        setFindingEmail(false);
                      }
                    }}
                    title="Find a verified email. 1 credit — refunded if no email is found."
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border border-brand/30 bg-brand/5 hover:bg-brand/10 text-brand text-[11px] font-semibold tracking-wide transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Search className={`w-3 h-3 ${findingEmail ? "animate-pulse" : ""}`} />
                    {findingEmail ? "Searching…" : (displayEmail ? "Find a better email" : "Find email")}
                    <span className="text-[10px] font-mono opacity-70 ml-0.5">1 cr · refund on miss</span>
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1.5 ml-auto shrink-0">
                <Button
                  data-testid={`reenrich-lead-${index}`}
                  variant="ghost"
                  size="sm"
                  disabled={reenriching}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    onReenrich?.(lead.id);
                  }}
                  className="text-ink-500 hover:text-ink-900 rounded-sm"
                  title="Re-enrich (uses 1 credit)"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${reenriching ? "animate-spin" : ""}`} />
                  Re-enrich
                </Button>
                <Button
                  data-testid={`delete-lead-${index}`}
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete?.(lead.id)}
                  className="text-ink-400 hover:text-accent rounded-sm"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Remove
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      <DraftEmailDialog
        open={draftOpen}
        onClose={() => setDraftOpen(false)}
        lead={lead}
      />
      <EnrollSequenceDialog
        open={enrollOpen}
        onClose={() => setEnrollOpen(false)}
        lead={lead}
      />
    </div>
  );
}
